import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../api";

/**
 * Loads countries once and caches states / cities for address forms.
 */
export function useAddressLocationData() {
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countriesError, setCountriesError] = useState(null);
  const statesCacheRef = useRef(new Map());
  const citiesCacheRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCountries(true);
      setCountriesError(null);
      try {
        const res = await API.getAddressCountries();
        const inner = res?.data?.data;
        const list = Array.isArray(inner) ? inner : [];
        if (!cancelled) setCountries(list);
      } catch (e) {
        if (!cancelled) setCountriesError(e);
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getStatesForCountry = useCallback(async (iso2) => {
    const code = String(iso2 || "")
      .trim()
      .toUpperCase();
    if (!code || code.length !== 2) return [];
    const cache = statesCacheRef.current;
    if (cache.has(code)) return cache.get(code);
    const res = await API.getAddressStates(code);
    const inner = res?.data?.data;
    const list = Array.isArray(inner) ? inner : [];
    cache.set(code, list);
    return list;
  }, []);

  const getCitiesForCountryState = useCallback(async (countryIso2, stateIso) => {
    const c = String(countryIso2 || "")
      .trim()
      .toUpperCase();
    const s = String(stateIso || "")
      .trim()
      .toUpperCase();
    if (!c || c.length !== 2 || !s) return [];
    const key = `${c}:${s}`;
    const cache = citiesCacheRef.current;
    if (cache.has(key)) return cache.get(key);
    const res = await API.getAddressCities(c, s);
    const inner = res?.data?.data;
    const list = Array.isArray(inner) ? inner : [];
    cache.set(key, list);
    return list;
  }, []);

  return {
    countries,
    loadingCountries,
    countriesError,
    getStatesForCountry,
    getCitiesForCountryState,
  };
}
